import { useEffect, useRef, useState } from "react";
import { LoroMap } from "loro-crdt";
import { Eye, FileCode2, Type } from "lucide-react";
import { clsx } from "clsx";
import { mountMarkdown } from "../../editors/MarkdownView";
import { mountProseMirror } from "../../editors/ProseMirrorView";
import { mountRendered } from "../../editors/RenderedView";
import { pmFocusTarget, usePageEditorContext } from "../../pages/pageEditorContext";
import { getMeta, setMeta } from "../../sync/persistence";
import { useT } from "../../i18n";
import type { BlockComponentProps } from "../registry";

export type SectionMode = "markdown" | "wysiwym" | "rendered";
const MODES: Array<{ mode: SectionMode; label: string; Icon: typeof Type }> = [
  { mode: "markdown", label: "section.mode.markdown", Icon: FileCode2 },
  { mode: "wysiwym", label: "section.mode.wysiwym", Icon: Type },
  { mode: "rendered", label: "section.mode.rendered", Icon: Eye },
];
const DEFAULT_KEY = "sectionModeDefault";
const isMode = (v: unknown): v is SectionMode => v === "markdown" || v === "wysiwym" || v === "rendered";

/**
 * Rich-text section (spec §7.4.2, §8.8, §8.19): one Loro `pm` container shown in the reader's chosen
 * mode — Markdown (CodeMirror), WYSIWYM (ProseMirror with a toolbar) or Rendered (read-only DOM). The
 * mode is a per-reader preference in IndexedDB, never in Loro (Q37).
 */
export function SectionBlock({ handle, node, readOnly, onExitUp, onExitDown, onFocus }: BlockComponentProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const ctx = usePageEditorContext();
  const key = `sectionMode:${handle.crdtDocId}:${node.id}`;
  // undefined while the stored preference loads; null when there is none
  const [chosen, setChosen] = useState<SectionMode | null | undefined>(undefined);
  const [fallback, setFallback] = useState<SectionMode>("wysiwym");
  useEffect(() => {
    let alive = true;
    void Promise.all([getMeta<SectionMode>(key), getMeta<SectionMode>(DEFAULT_KEY)]).then(([own, def]) => {
      if (!alive) return;
      if (isMode(def)) setFallback(def);
      setChosen(isMode(own) ? own : null);
    });
    return () => {
      alive = false;
    };
  }, [key]);
  const mode: SectionMode | null = chosen === undefined ? null : (chosen ?? (readOnly ? "rendered" : fallback));

  const choose = (next: SectionMode) => {
    setChosen(next);
    setFallback(next);
    void setMeta(key, next);
    void setMeta(DEFAULT_KEY, next);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el || !mode) return;
    const body = node.data.get("body");
    if (!(body instanceof LoroMap)) return;
    let pm = body.get("pm");
    if (!(pm instanceof LoroMap)) {
      if (readOnly) return;
      pm = body.setContainer("pm", new LoroMap());
      handle.doc.commit({ origin: "sys:init" });
    }
    const containerId = (pm as LoroMap).id;
    if (mode === "rendered") {
      const r = mountRendered(el, { doc: handle.doc, containerId, emptyText: t("section.empty") });
      return () => r.destroy();
    }
    if (mode === "markdown") {
      const m = mountMarkdown(el, { doc: handle.doc, containerId, readOnly, onExitUp, onExitDown, onFocus });
      ctx.registerView(node.id, {
        focusEdge: (edge) => {
          m.view.focus();
          const pos = edge === "end" ? m.view.state.doc.length : 0;
          m.view.dispatch({ selection: { anchor: pos } });
        },
      });
      return () => {
        ctx.unregisterView(node.id);
        m.destroy();
      };
    }
    const view = mountProseMirror(el, { doc: handle.doc, containerId, undoManager: ctx.undoManager, presence: ctx.cursors, readOnly, toolbar: true, t, onExitUp, onExitDown, onFocus });
    ctx.registerView(node.id, pmFocusTarget(view));
    return () => {
      ctx.unregisterView(node.id);
      view.destroy();
    };
    // `t`: a change of interface language remounts, so the toolbar speaks the new one (§8.23)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle.doc, node.id, readOnly, mode, t]);

  return (
    <div className="group/section relative" data-section-mode={mode ?? ""}>
      <div
        className={clsx(
          "absolute right-1 top-1 z-10 flex overflow-hidden rounded border border-neutral-300 bg-white text-neutral-500 opacity-0 transition focus-within:opacity-100 group-hover/section:opacity-100 dark:border-neutral-700 dark:bg-neutral-900",
          mode !== "wysiwym" && "opacity-60",
        )}
        role="group"
        aria-label={t("section.viewMode")}
        data-testid="section-modes"
      >
        {MODES.map(({ mode: m, label, Icon }, i) => (
          <button
            key={m}
            type="button"
            className={clsx("px-1.5 py-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800", i > 0 && "border-l border-neutral-300 dark:border-neutral-700", mode === m && "bg-neutral-200 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100")}
            aria-label={t(label)}
            title={t(label)}
            aria-pressed={mode === m}
            data-testid={`section-mode-${m}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(m)}
          >
            <Icon size={13} />
          </button>
        ))}
      </div>
      <div ref={ref} className={clsx("prose-block px-1 py-0.5", mode === "wysiwym" && !readOnly && "pt-0")} data-testid="section-editor" />
    </div>
  );
}
